// English string catalogue. Mirrors he.ts so the runtime can switch the entire UI
// without changing component logic.

export const en = {
  app: {
    name: "Whisperz Secured Conferencing Systems",
    brand: "Whisperz Secured Conferencing Systems",
    startupError: "The system failed to load. Please refresh or contact support."
  },
  nav: {
    home: "Home",
    schedule: "Meetings",
    admin: "Admin",
    switchLanguage: "Switch language",
    placeholder: "This screen will be built in the next development phases. See"
  },
  banner: {
    reconnecting: "Reconnecting...",
    recordingOn: "Recorded"
  },
  launch: {
    caseContext: "Case details",
    driver: "Driver ID",
    caseRef: "Reference",
    system: "Source system"
  },
  call: {
    precallTitle: "Select an officer for the hearing",
    search: "Search officer...",
    available: "Available",
    unavailable: "Unavailable",
    callAction: "Call",
    noOfficers: "No officers are available right now.",
    callingTitle: "Calling...",
    cancel: "Cancel",
    incomingTitle: "Incoming call",
    accept: "Answer",
    decline: "Decline",
    endCall: "End call",
    endedTitle: "The call has ended",
    backToList: "Back to list",
    mute: "Mute",
    unmute: "Unmute",
    camOff: "Turn camera off",
    camOn: "Turn camera on",
    selfPanel: "Field officer and citizen",
    officerPanel: "Traffic officer",
    availabilityToggle: "Availability status",
    shareScreen: "Share screen",
    stopShare: "Stop sharing",
    screenSharing: "Screen sharing",
    controlsLabel: "Call controls",
    fullscreen: "Full screen",
    exitFullscreen: "Exit full screen",
    officerStation: "Officer station",
    waitingTitle: "Waiting for an incoming call",
    waitingStation: "This station is ready and available to receive incoming calls.",
    localPreview: "Local preview",
    tokenExpired: "The connection expired. Please open the demo link again.",
    connectFailed: "Could not connect to the call service. Refresh the page or open the demo link again.",
    missingCallId: "No call identifier was found for starting the call.",
    hubNotReady: "The call service is not connected yet. Refresh the page or open the demo link again.",
    startFailed: "Could not start the call.",
    callerFallback: "Officer"
  },
  readiness: {
    title: "Camera and microphone check",
    subtitle: "Make sure the camera and microphone work before starting the call.",
    camera: "Camera",
    microphone: "Microphone",
    cameraOn: "Camera on",
    cameraOff: "Camera off",
    micOn: "Microphone on",
    micOff: "Microphone muted",
    cameraOffPreview: "Camera is off",
    continueTraffic: "Ready - start receiving calls",
    continueField: "Continue to officer selection",
    retry: "Try again",
    requesting: "Requesting camera and microphone access...",
    noDevice: "No available camera or microphone was found. Connect a device and try again.",
    trackEnded: "The camera or microphone stopped working. Check the device and try again.",
    autoChecking: "Checking your camera and microphone. You will join automatically.",
    autoJoining: "Camera and microphone are working. Joining...",
    autoWaiting: "Waiting for a working camera and microphone. Make sure both are switched on and not used by another app."
  },
  availability: {
    available: "Available",
    unavailable: "Unavailable",
    title: "Availability status"
  },
  schedule: {
    title: "Scheduled meetings",
    none: "No scheduled meetings.",
    join: "Join",
    cancel: "Cancel",
    start: "Start",
    duration: "Duration (min)",
    caseRef: "Reference"
  },
  admin: {
    title: "Management Console",
    officers: "Officers",
    recordings: "Recordings",
    dashboard: "Dashboard",
    config: "Configuration",
    settings: "Settings",
    notAuthorized: "Administrator permission is required to view this console. Sign in with an administrator or IT viewer account.",
    sessionExpired: "The session expired. Please sign in again."
  },
  auth: {
    title: "Administrator sign-in",
    hint: "Sign in with the administrator account to manage the system and scheduled meetings.",
    username: "Username",
    password: "Password",
    submit: "Sign in",
    checking: "Signing in...",
    required: "Enter a username and password.",
    failed: "The username or password is incorrect.",
    locked: "Too many failed attempts. Wait a few minutes and try again.",
    unavailable: "Sign-in is temporarily unavailable. Please try again or contact support.",
    signOut: "Sign out",
    signedInAs: "Signed in as",
    sessionExpired: "Your session expired. Please sign in again.",
    defaultPasswordWarning: "You are signed in with the default password. Change it now.",
    goToSettings: "Change password"
  },
  settings: {
    passwordTitle: "Change administrator password",
    passwordHint: "Use 10-128 characters. Changing the password signs out every other administrator session.",
    current: "Current password",
    next: "New password",
    confirm: "Confirm new password",
    submit: "Change password",
    saving: "Saving...",
    success: "The password was changed.",
    errors: {
      currentRequired: "Enter the current password.",
      currentIncorrect: "The current password is incorrect.",
      mismatch: "The new password and its confirmation do not match.",
      length: "The new password must be 10-128 characters long.",
      notAllowed: "The new password must differ from the current password, the default password and the username.",
      locked: "Too many failed attempts. Wait a few minutes and try again."
    }
  },
  pub: {
    loading: "Loading...",
    welcomeTitle: "Welcome",
    welcomeText: "Enter your phone number to start a secure video call.",
    phoneLabel: "Phone number",
    phonePlaceholder: "+1-868-1234567",
    continue: "Continue",
    checking: "Checking...",
    otpTitle: "Enter your verification code",
    otpTextWhatsApp: "We sent a 6-digit verification code to your WhatsApp. Enter it to continue:",
    otpTextMock: "Test mode: enter the verification code you were given for testing. Phone:",
    otpLabel: "Verification code",
    verify: "Verify",
    changePhone: "Use a different phone number",
    guestTitle: "Who would you like to call?",
    guestText: "Enter the other participant's phone number. They do not need to be registered.",
    guestLabel: "Other participant's phone number",
    guestContinue: "Continue to device check",
    signedInAs: "Signed in as",
    readyAction: "Ready",
    preparing: "Preparing the invitation...",
    waitingTitle: "Waiting for the other participant",
    waitingText: "The call starts automatically as soon as the other participant is ready.",
    calling: "Calling",
    guestStates: {
      notOpened: "The invitation has not been opened yet.",
      joining: "The other participant opened the invitation and is checking their devices.",
      ready: "The other participant is ready. Starting the call...",
      away: "The other participant left the page. They can reopen the invitation link."
    },
    delivery: {
      "mock-not-sent": "Test mode: WhatsApp delivery is turned off and no message was sent. Copy the invitation link and share it with the other participant.",
      "waha-sending": "Sending the invitation via WhatsApp was not confirmed. It will not be resent automatically — copy the link if the other participant did not get it.",
      "waha-accepted": "The invitation was accepted by the WhatsApp service for delivery to the other participant. This does not confirm it was received — you can also copy the link.",
      "waha-failed": "WhatsApp could not send the invitation. Copy the link and share it another way.",
      "waha-unknown": "The WhatsApp service did not confirm the invitation. It was not resent automatically — copy the link if the other participant did not get it.",
      "delivery-unavailable": "WhatsApp delivery is unavailable right now, so no message was sent. Copy the link and share it another way.",
      pending: "Preparing the invitation..."
    } as Record<string, string>,
    copyLink: "Copy invitation link",
    copied: "Invitation link copied.",
    copyFailed: "The link could not be copied automatically. Your browser blocked clipboard access.",
    inviteExpires: "Invitation valid until",
    cancel: "Cancel call",
    startOver: "Start over",
    starting: "Starting the call...",
    guestTitleInvited: "You are invited to a video call",
    invitedBy: "Invited by",
    guestText2: "Allow camera and microphone access. You join automatically as soon as both work.",
    guestTextManual: "Check your camera and microphone, then select Ready.",
    guestWaitingTitle: "Waiting for the call to start",
    guestWaitingText: "The call starts automatically.",
    initiatorAway: "The person who invited you is not connected right now. Please keep this page open.",
    leave: "Leave",
    endedTitle: "The call has ended",
    endedText: "Thank you. You can close this page.",
    closedTitle: "This call is not available",
    closed: {
      cancelled: "The call was cancelled.",
      expired: "This invitation has expired.",
      invalid: "This invitation link is invalid or has expired.",
      signedOut: "Your session has ended. Please start again."
    },
    errors: {
      invalidPhone: "Enter a valid phone number.",
      cannotContinue: "We cannot continue with this phone number. Check the number or contact your administrator.",
      tooMany: "Too many attempts. Wait a few minutes and try again.",
      codeRejected: "The code is incorrect or has expired.",
      codeRequired: "Enter the verification code.",
      sameParty: "Enter a phone number different from your own.",
      unavailable: "The service is temporarily unavailable. Please try again.",
      deliveryUnavailable: "We could not send the verification code right now. Please try again later.",
      generic: "Something went wrong. Please try again."
    }
  },
  waha: {
    title: "WhatsApp delivery (WAHA)",
    loading: "Loading WhatsApp settings...",
    activeTitle: "WhatsApp delivery is ON.",
    activeText: "New sign-in codes and call invitations are sent through WhatsApp. Turning it off returns new sign-ins to the test mode.",
    offTitle: "WhatsApp delivery is OFF.",
    offText: "Sign-in uses the test mode and invitations are not sent; the caller copies the link. Saved settings stay encrypted.",
    stateEmpty: "Not configured yet.",
    stateDraft: "Saved as a draft — the service URL and API key are both needed to complete it.",
    stateComplete: "Configuration complete.",
    baseUrl: "Service URL (HTTPS)",
    baseUrlHint: "The WAHA service address, e.g. https://waha.example.internal — https only, no user name, query or fragment. Local and link-local addresses are not allowed.",
    session: "Session name",
    apiKey: "API key",
    apiKeyConfigured: "Configured — leave blank to keep",
    apiKeyEmpty: "Enter the API key",
    apiKeyKeepHint: "Write-only. Leave blank to keep the saved key, type a new one to replace it.",
    apiKeyWriteOnlyHint: "Write-only. The key is stored encrypted and is never shown again.",
    clearApiKey: "Remove the saved API key",
    clearNeedsDisable: "To remove the key, first turn WhatsApp delivery off and save.",
    testRecipient: "Test recipient phone",
    testRecipientHint: "\"Send test message\" below sends only to this saved number. Saving does not send anything.",
    enable: "Enable WhatsApp delivery",
    enableHint: "When on, every new sign-in code and every new call invitation is sent as a WhatsApp message. Requires a complete configuration.",
    save: "Save settings",
    saving: "Saving...",
    savedActive: "Settings saved (encrypted). WhatsApp delivery is on.",
    savedOff: "Settings saved (encrypted). WhatsApp delivery is off.",
    checkTitle: "Check connection",
    checkHint: "Asks the WAHA service whether the saved session is ready. No message is sent.",
    check: "Check connection",
    checking: "Checking...",
    actionsNeedComplete: "Save a complete configuration (service URL and API key) to use these actions.",
    actionsNeedSave: "Save your changes first — these actions use the saved settings.",
    checkResults: {
      working: "Connected: the WhatsApp session is ready (status WORKING).",
      "session-not-working": "Connected, but the WhatsApp session is not ready. Open the WAHA dashboard to start or pair it.",
      unauthorized: "The WAHA service rejected the API key.",
      "session-not-found": "The WAHA service has no session with this name.",
      unreachable: "The WAHA service could not be reached (network or certificate problem).",
      timeout: "The WAHA service did not answer in time.",
      "blocked-target": "The service address resolves to a blocked network address.",
      failed: "The WAHA service returned an unexpected answer."
    },
    testTitle: "Send test message",
    testWarning: "This sends one real WhatsApp message (a fixed Whisperz test text) to the saved test recipient only. It works while delivery is off and is never sent automatically.",
    testTarget: "Sends to the saved test recipient:",
    testTargetNone: "none saved",
    testNeedsRecipient: "Save a test recipient phone above to send a test message.",
    sendTest: "Send test message",
    sendingTest: "Sending...",
    testResults: {
      accepted: "Accepted by the WAHA service for delivery. Check the phone — acceptance does not confirm it was received.",
      rejected: "The WAHA service did not send the message.",
      unknown: "No confirmation from the WAHA service. The message may or may not have been sent; it was not retried."
    },
    errors: {
      urlInvalid: "Enter an https:// service URL without user name, query or fragment.",
      urlTarget: "This service address is not allowed (local, link-local or reserved).",
      sessionInvalid: "The session name must be 1-64 letters, digits, '-' or '_'.",
      apiKeyInvalid: "The API key must be 1-512 printable characters without line breaks.",
      recipientInvalid: "Enter a valid phone number.",
      clearConflict: "Either enter a new API key or remove the saved one, not both.",
      enableIncomplete: "WhatsApp delivery can be turned on only with a service URL, session and API key.",
      disableFirst: "Turn WhatsApp delivery off before removing the service URL or API key.",
      incomplete: "Save a complete configuration first.",
      recipientRequired: "Save a test recipient phone first.",
      tooMany: "Too many attempts. Wait a few minutes and try again.",
      tooManyTests: "Too many test messages. Wait before sending another one.",
      httpsRequired: "These settings can only be used over a secure (HTTPS) connection.",
      unavailable: "The secure settings storage is unavailable. Ask the system administrator to check the server's key and storage provisioning.",
      generic: "The action could not be completed. Please try again."
    }
  },  regUsers: {
    title: "Registered users",
    hint: "Only active registered users can start a call from the public site.",
    phone: "Phone number",
    name: "Display name",
    status: "Status",
    actions: "Actions",
    active: "Active",
    disabled: "Disabled",
    add: "Add user",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    enable: "Enable",
    disable: "Disable",
    remove: "Remove",
    removeConfirm: "Remove this user? A user with call history is deactivated instead.",
    removed: "The user was removed.",
    deactivated: "The user has call history and was deactivated instead.",
    saved: "Saved.",
    none: "No registered users yet.",
    loading: "Loading registered users...",
    errors: {
      invalidPhone: "Enter a valid phone number.",
      nameRequired: "Enter a display name (up to 128 characters).",
      duplicate: "A registered user with this phone number already exists.",
      notFound: "The user no longer exists. Refresh the list.",
      generic: "The change could not be saved. Please try again."
    }
  },
  dash: {
    subtitle: "Operational status of the hearing system",
    updated: "Updated",
    refresh: "Refresh",
    sectionStatus: "Operational status",
    sectionHealth: "Service health",
    sectionRecordings: "Recording status",
    sectionOfficers: "Officer availability",
    sectionActivity: "Recent activity",
    sectionMetrics: "Infrastructure and monitoring metrics",
    officersAvailable: "Available officers",
    officersUnavailable: "Unavailable",
    recInProgress: "Recordings in progress",
    recCompleted: "Completed recordings",
    recFailed: "Failed recordings",
    recQueued: "Queued",
    recSigned: "Digitally signed",
    storageUsed: "Storage used",
    totalMinutes: "Recording minutes",
    latestRecording: "Latest recording",
    of: "of",
    serviceUp: "Up",
    serviceDown: "Unavailable",
    metricsNotReported: "Infrastructure monitoring metrics have not reported yet. The operational data above reflects the current system state.",
    noActivity: "No recent recording activity.",
    secondsUnit: "sec",
    services: {
      "app-service": "Application service",
      "media-service": "Media service",
      "compositing-worker": "Recording processor"
    } as Record<string, string>,
    kpi: {
      vc_active_calls: "Active calls",
      vc_configured_capacity: "Configured capacity",
      vc_available_slots: "Available slots",
      vc_capacity_utilization_ratio: "Capacity utilization",
      vc_call_setup_seconds_p95: "Call setup time (p95)",
      vc_call_failure_ratio: "Call failure ratio",
      vc_reconnect_ratio: "Reconnect ratio",
      vc_compositing_queue_depth: "Recording processing queue",
      vc_compositing_latency_seconds_p95: "Processing time (p95)",
      vc_compositing_failed_total: "Processing failures",
      vc_recording_capture_faults_total: "Capture faults",
      vc_scratch_store_free_ratio: "Scratch storage free",
      vc_recording_store_free_ratio: "Recording storage free",
      vc_cpu_headroom_ratio: "CPU headroom"
    } as Record<string, string>
  },
  rec: {
    search: "Search (system / reference / ID / file name)",
    statusAll: "All statuses",
    statusCompleted: "Completed",
    statusQueued: "Queued",
    statusCompositing: "Processing",
    statusCapturing: "Capturing",
    statusFailed: "Failed",
    refresh: "Refresh",
    none: "No recordings found.",
    colStatus: "Status",
    colCall: "Call",
    colCase: "System / case / driver",
    colDuration: "Duration",
    colSize: "Size",
    colCreated: "Created",
    colCompleted: "Completed",
    play: "Play",
    download: "Download",
    manifest: "Manifest",
    signature: "Signature",
    details: "Recording details",
    notPlayable: "This recording is not available for playback yet (not completed or missing file).",
    seconds: "sec",
    close: "Close",
    loading: "Loading recording details...",
    selectHint: "Select a recording from the list to play or download it.",
    authTitle: "Recording playback sign-in",
    authHint: "A separate username and password are required only to play recordings.",
    username: "Username",
    password: "Password",
    authSubmit: "Sign in",
    authChecking: "Checking...",
    authFailed: "The username or password is incorrect.",
    authExpired: "Recording playback permission expired. Please sign in again."
  },
  errors: {
    "VC-AUTH-001": "The session expired. Please sign in again.",
    "VC-AUTH-002": "You are not authorized to perform this action.",
    "VC-AUTH-010": "The username or password is incorrect.",
    "VC-AUTH-011": "Too many failed attempts. Wait a few minutes and try again.",
    "VC-AUTH-016": "Sign-in is temporarily unavailable. Please try again or contact support.",
    "VC-LAUNCH-001": "Launch details are missing or invalid. Please contact the hosting system.",
    "VC-CALL-001": "The officer is not available right now.",
    "VC-CALL-002": "No answer. Try again or choose another officer.",
    "VC-CALL-003": "The officer declined the call.",
    "VC-CALL-004": "The system is at full capacity. Please try again in a few minutes.",
    "VC-MEDIA-001": "Camera and microphone access is required.",
    "VC-MEDIA-002": "Media error. Reconnecting...",
    "VC-MEDIA-003": "The connection failed. The call ended and the recording was saved.",
    "VC-MEDIA-004": "A secure HTTPS connection is required to access the camera and microphone. Open the demo with a secure URL.",
    "VC-REC-001": "Recording error. Please contact support.",
    "VC-CFG-001": "A configuration value is invalid.",
    "VC-SCHED-001": "Meeting details are invalid. Check duration and participants.",
    "VC-SCHED-002": "A completed meeting cannot be canceled.",
    "VC-SCHED-404": "Meeting not found.",
    "VC-RESYNC": "The connection resumed. Synchronizing call state...",
    "VC-SYS-001": "An error occurred. Please try again or contact support."
  }
} as const;
